import React, { useEffect, useRef } from "react";

export default function MaintenancePage() {
  const canvasRef = useRef(null);

  // Subtle floating particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const PARTICLE_COUNT = 55;
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.5 + 0.4,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      alpha: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 223, 130, ${p.alpha})`;
        ctx.fill();
      });

      // Draw faint connecting lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 223, 130, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0A0A0F",
        display: "flex",
        flexDirection: "column",
        zIndex: 9999,
        fontFamily: "'Satoshi', 'Inter', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />

      {/* Radial glow */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse 60% 50% at 50% 60%, rgba(0,223,130,0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Top bar */}
      <header
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "28px 48px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Logo mark */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <circle cx="16" cy="16" r="15" stroke="#00DF82" strokeWidth="1.5" />
            <circle cx="16" cy="16" r="5" fill="#00DF82" opacity="0.9" />
            <line x1="16" y1="1" x2="16" y2="9" stroke="#00DF82" strokeWidth="1.5" />
            <line x1="16" y1="23" x2="16" y2="31" stroke="#00DF82" strokeWidth="1.5" />
            <line x1="1" y1="16" x2="9" y2="16" stroke="#00DF82" strokeWidth="1.5" />
            <line x1="23" y1="16" x2="31" y2="16" stroke="#00DF82" strokeWidth="1.5" />
          </svg>
          <span
            style={{
              color: "#EDEDED",
              fontSize: "15px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Péptidos Costa Rica
          </span>
        </div>


      </header>

      {/* Main content */}
      <main
        style={{
          position: "relative",
          zIndex: 10,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 48px",
          maxWidth: "900px",
        }}
      >
        {/* Status pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "rgba(0,223,130,0.08)",
            border: "1px solid rgba(0,223,130,0.25)",
            borderRadius: "999px",
            padding: "6px 14px",
            marginBottom: "40px",
            width: "fit-content",
          }}
        >
          <span
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              background: "#00DF82",
              display: "inline-block",
              animation: "pulse-dot 1.8s ease-in-out infinite",
            }}
          />
          <span
            style={{
              color: "#00DF82",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Under Maintenance
          </span>
        </div>

        <h1
          style={{
            color: "#EDEDED",
            fontSize: "clamp(2.4rem, 5vw, 4.2rem)",
            fontWeight: 700,
            lineHeight: 1.12,
            letterSpacing: "-0.02em",
            marginBottom: "24px",
            fontFamily: "'Cabinet Grotesk', 'Satoshi', sans-serif",
          }}
        >
          We're working on
          <br />
          <span style={{ color: "#00DF82" }}>something amazing.</span>
        </h1>

        <p
          style={{
            color: "rgba(237,237,237,0.5)",
            fontSize: "clamp(1rem, 2vw, 1.2rem)",
            lineHeight: 1.7,
            maxWidth: "520px",
          }}
        >
          Pardon our dust — check back soon.
        </p>
      </main>

      {/* Bottom bar */}
      <footer
        style={{
          position: "relative",
          zIndex: 10,
          padding: "20px 48px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <span style={{ color: "rgba(237,237,237,0.25)", fontSize: "12px" }}>
          © {new Date().getFullYear()} Péptidos Costa Rica. All rights reserved.
        </span>
        <span style={{ color: "rgba(237,237,237,0.25)", fontSize: "12px" }}>
          peptidoscostarica.com
        </span>
      </footer>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
      `}</style>
    </div>
  );
}
